import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { supabase } from '../supabaseClient';
import Modal from './Modal';
import AutoSizeTextarea from './AutoSizeTextarea';
import '../lib/datepicker-theme.css';
import { fmtDate } from '../lib/dates';

// Re-open brief modal — opens from WinnerBanner's "Re-open Brief"
// button and from the BriefDetail header MoreMenu. Replaces the
// legacy window.prompt() flow with a structured form that captures
// a reason key, free-text internal notes, and an optional decision
// to re-open the brief to other respondents (default ON).
//
// On submit we do everything inline (no separate API endpoint —
// supabase-js writes are the existing pattern across the app):
//   1. INSERT brief_award_history row (snapshot of the cleared award
//      plus reason / notes / also_reopened / new deadline)
//   2. UPDATE briefs:
//        - if also_reopened: status='sent', clear awarded_* fields,
//          set required_response_time = new deadline
//        - else:             keep status='closed', clear awarded_*
//          fields only. Brief stays closed but renders as a
//          "previously-awarded, withdrawn" closed brief.
//   3. UPDATE the awarded supplier's brief_recipients row:
//      status='withdrawn'. The supplier portal's briefBucket()
//      filters 'withdrawn' rows out of every tab so the brief
//      silently disappears from their view — per design, all
//      direct supplier comms in this flow happen outside Alchemy.
//
// We do NOT send any emails. We do NOT modify other respondents'
// brief_recipients rows — when the brief flips back to status='sent',
// their existing rows naturally route them back to the right tab
// (pending → Awaiting, responded → Submitted, etc).
//
// Reason keys are stable; labels can change later without breaking
// audit data. Notes require ≥10 non-whitespace characters, matching
// the discipline of the compliance review flow.

const WITHDRAWAL_REASONS = [
  { key: 'supplier_dropped_out',       label: 'Supplier dropped out' },
  { key: 'supplier_compliance_lapsed', label: 'Supplier compliance lapsed' },
  { key: 'supplier_unresponsive',      label: 'Supplier unresponsive' },
  { key: 'fusion_changed_scope',       label: 'Fusion changed scope' },
  { key: 'event_cancelled',            label: 'Event cancelled' },
  { key: 'client_preference_changed',  label: 'Client preference changed' },
  { key: 'other',                      label: 'Other' },
];
const REASON_LABEL_BY_KEY = Object.fromEntries(WITHDRAWAL_REASONS.map(r => [r.key, r.label]));

const MIN_NOTES_LENGTH = 10;

// Working-day arithmetic — skips Sat (6) and Sun (0). Defaults the
// new deadline to "today + 5 working days" so admins land on a
// sensible, weekend-aware date without thinking.
const addWorkingDays = (start, days) => {
  const d = new Date(start);
  d.setHours(12, 0, 0, 0); // mid-day so DST + tz boundaries don't trip us
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return d;
};

const formatBritish = (date) => fmtDate(date, '');

const Spinner = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="animate-spin">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" strokeOpacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

export default function ReopenBriefModal({
  brief,
  winner,           // brief_recipients row for the awarded supplier (with suppliers join)
  otherRespondents, // brief_recipients rows for non-winning respondents
  currentInternalUser,
  onClose,
  onComplete,       // (result) => void — result is { alsoReopened, winnerName }
}) {
  const supplierName = winner?.suppliers?.supplier_name || brief?.awarded_supplier?.supplier_name || 'this supplier';
  const otherCount = otherRespondents?.length || 0;
  // Re-open is ALWAYS allowed: an admin re-opening an awarded brief wants it
  // active again so they can re-award — whether or not there were other
  // respondents (they may re-award the same supplier, or add new recipients).
  // Previously this required otherCount > 0, so an award to the sole respondent
  // could only be WITHDRAWN (left closed), never re-opened — and Complete Brief
  // (which needs status='sent') then vanished with no one-step way back.
  const canReopen = true;

  const defaultDeadline = useMemo(() => addWorkingDays(new Date(), 5), []);
  const originalDeadline = brief?.required_response_time ? new Date(brief.required_response_time) : null;

  const [reasonKey, setReasonKey] = useState('');
  const [notes, setNotes] = useState('');
  const [alsoReopen, setAlsoReopen] = useState(canReopen); // default ON when possible
  const [newDeadline, setNewDeadline] = useState(defaultDeadline);
  const [state, setState] = useState('form'); // form | submitting | error
  const [errorMessage, setErrorMessage] = useState(null);

  // If the brief has zero other respondents, the re-open option is
  // disabled and the modal is effectively a "Withdraw Award" form.
  useEffect(() => {
    if (!canReopen) setAlsoReopen(false);
  }, [canReopen]);

  const trimmedNotes = notes.trim();
  const notesValid = trimmedNotes.length >= MIN_NOTES_LENGTH;
  const reasonValid = !!reasonKey;

  // Deadline validation only matters when we're re-opening. Must be
  // strictly after today. If the brief has a future event date we
  // also disallow deadlines past the event itself (no point asking
  // suppliers to quote on something they can't deliver).
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const eventDate = brief?.event_date ? new Date(brief.event_date) : null;
  let deadlineError = null;
  if (alsoReopen) {
    if (!newDeadline) deadlineError = 'Choose a new response deadline.';
    else if (newDeadline.getTime() <= today.getTime()) deadlineError = 'Deadline must be after today.';
    else if (eventDate && newDeadline.getTime() > eventDate.getTime()) {
      deadlineError = `Deadline must be on or before the event date (${formatBritish(eventDate)}).`;
    }
  }
  const deadlineValid = !alsoReopen || !deadlineError;
  const canSubmit = reasonValid && notesValid && deadlineValid && state !== 'submitting';

  const submitting = state === 'submitting';
  const safeClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [submitting, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !brief?.id) return;
    setState('submitting');
    setErrorMessage(null);

    // 1. Snapshot the cleared award. Mirrors the field-set used by the
    // legacy handleReopenBrief — extends it with also_reopened, the
    // structured reason key (stored alongside the human-readable
    // label in reopen_reason, separated by " — " so the timeline
    // detail line reads naturally), and the long-form notes.
    const reasonLabel = REASON_LABEL_BY_KEY[reasonKey] || reasonKey;
    const reopenReasonValue = `${reasonKey} — ${reasonLabel}`;
    const { error: histErr } = await supabase.from('brief_award_history').insert([{
      brief_id: brief.id,
      awarded_supplier_id: brief.awarded_supplier_id || null,
      awarded_supplier_name: brief.awarded_supplier?.supplier_name || winner?.suppliers?.supplier_name || null,
      awarded_amount: brief.awarded_amount || null,
      award_decision_reason: brief.award_decision_reason || null,
      awarded_at: brief.closed_at || null,
      awarded_by: brief.closed_by || null,
      awarded_by_name: brief.closed_by_user?.full_name || null,
      reopened_by: currentInternalUser?.id || null,
      reopened_by_name: currentInternalUser?.full_name || null,
      reopen_reason: reopenReasonValue,
      internal_notes: trimmedNotes,
      also_reopened: alsoReopen,
    }]);
    if (histErr) {
      setErrorMessage(`Couldn't save the audit record — ${histErr.message}. No changes have been made.`);
      setState('error');
      return;
    }

    // 2. Update the brief row. Always clear the live award fields.
    // If also_reopened, flip status back to 'sent' and update the
    // response deadline. Otherwise leave status='closed' so the
    // brief renders as a closed brief with no awardee (the history
    // badge surfaces who it was previously awarded to).
    const briefUpdate = {
      awarded_supplier_id: null,
      awarded_amount: null,
      award_decision_reason: null,
    };
    if (alsoReopen) {
      briefUpdate.status = 'sent';
      briefUpdate.closed_at = null;
      briefUpdate.closed_by = null;
      if (newDeadline) briefUpdate.required_response_time = newDeadline.toISOString();
    }
    const { error: briefErr } = await supabase.from('briefs').update(briefUpdate).eq('id', brief.id);
    if (briefErr) {
      setErrorMessage(`Audit saved but the brief update failed — ${briefErr.message}. Refresh and try again.`);
      setState('error');
      return;
    }

    // On re-open, reset the per-recipient deadline overrides so every supplier
    // falls back to the NEW brief-level deadline. Without this, stale overrides
    // from the original send (or a prior extension) — often now in the past —
    // keep suppliers on a lapsed deadline even though the brief has reopened.
    // Re-arm the 24h reminder + clear any outstanding extension request too.
    if (alsoReopen) {
      const { error: recResetErr } = await supabase
        .from('brief_recipients')
        .update({ response_deadline: null, reminder_sent_at: null, extension_requested_at: null, extension_request_note: null })
        .eq('brief_id', brief.id);
      if (recResetErr) {
        setErrorMessage(`Brief re-opened but supplier deadlines couldn't be refreshed — ${recResetErr.message}. Use Extend Deadline to set them.`);
        setState('error');
        return;
      }
    }

    // 3. Withdraw the awarded supplier's recipient row so the brief
    // disappears from their portal. Only update if we have a winner
    // row to act on — defensive in case the data drifted between
    // open and submit.
    if (winner?.id) {
      const { error: recErr } = await supabase
        .from('brief_recipients')
        .update({ status: 'withdrawn' })
        .eq('id', winner.id);
      if (recErr) {
        setErrorMessage(`Brief updated but couldn't withdraw the supplier's recipient row — ${recErr.message}. They may still see the brief in their portal until this is corrected.`);
        setState('error');
        return;
      }
    }

    onComplete?.({ alsoReopened: alsoReopen, winnerName: supplierName });
  }, [canSubmit, brief, winner, currentInternalUser, reasonKey, trimmedNotes, alsoReopen, newDeadline, supplierName, onComplete]);

  const submitLabel = alsoReopen ? 'Re-open Brief' : 'Withdraw Award';
  const winnerInitial = (supplierName?.[0] || '?').toUpperCase();
  const awardedAmountText = brief?.awarded_amount != null
    ? `£${Number(brief.awarded_amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : null;

  const deadlineHint = (() => {
    if (!originalDeadline) return 'Default: 5 working days from today.';
    const passed = originalDeadline.getTime() < today.getTime();
    return passed
      ? `Originally ${formatBritish(originalDeadline)} (passed). Default: 5 working days from today.`
      : `Originally ${formatBritish(originalDeadline)}. Default: 5 working days from today.`;
  })();

  return (
    <Modal open onClose={safeClose} maxWidth={500} stripe={false} ariaLabel="Re-open brief">
      {/* Red-toned header — this is a serious action, not a routine one.
          Stripe is off; we render our own red-accent eyebrow instead. */}
      <div className="px-7 pt-5 pb-4 border-b border-line flex items-start justify-between gap-4 flex-shrink-0">
        <div className="min-w-0 flex-1">
          <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#E0290A] mb-1.5">
            Withdraw &amp; Re-open
          </div>
          <div className="font-['Metropolis'] text-lg font-bold text-ink mb-1 truncate">Re-open brief</div>
          <div className="font-['Metropolis'] text-xs text-[#51534A]">
            This withdraws the current award and optionally re-opens the brief to previous respondents.
          </div>
        </div>
        {!submitting && (
          <button
            type="button"
            onClick={safeClose}
            aria-label="Close"
            className="w-8 h-8 flex items-center justify-center bg-transparent border border-line rounded text-[#51534A] hover:text-ink hover:border-[#DADCCD] transition-colors flex-shrink-0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      <Modal.Body>
        {/* Currently awarded summary */}
        <div className="flex items-center gap-3 bg-white border border-line rounded-md px-3.5 py-3 mb-5">
          <div className="w-8 h-8 rounded bg-[#92D400] text-[#131313] flex items-center justify-center font-extrabold text-sm flex-shrink-0">
            {winnerInitial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-0.5">Currently awarded to</div>
            <div className="font-['Metropolis'] text-[13px] font-bold text-ink truncate">
              {supplierName}{awardedAmountText ? ` · ${awardedAmountText}` : ''}
            </div>
          </div>
        </div>

        {/* Reason */}
        <div className="mb-4">
          <label htmlFor="reopen-reason" className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-1.5">
            Reason <span className="text-[#E0290A]">*</span>
          </label>
          <select
            id="reopen-reason"
            value={reasonKey}
            onChange={(e) => setReasonKey(e.target.value)}
            disabled={submitting}
            className="w-full bg-white border border-line rounded-md px-3 py-2.5 font-['Metropolis'] text-[13px] text-ink focus:outline-none focus:border-[#DADCCD] disabled:opacity-50">
            <option value="">Choose a reason…</option>
            {WITHDRAWAL_REASONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          <p className="font-['Metropolis'] text-[10px] text-[#51534A] mt-1.5">For internal audit only. Not sent to suppliers.</p>
        </div>

        {/* Internal notes */}
        <div className="mb-4">
          <label htmlFor="reopen-notes" className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-1.5">
            Internal notes <span className="text-[#E0290A]">*</span>
          </label>
          <AutoSizeTextarea
            id="reopen-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={submitting}
            minRows={4}
            placeholder="Context for the audit log — what happened, when, anything Fusion needs to remember later."
            className="w-full bg-white border border-line rounded-md px-3 py-2.5 font-['Metropolis'] text-[13px] text-ink placeholder:text-[#51534A] focus:outline-none focus:border-[#DADCCD] disabled:opacity-50" />
          <p className="font-['Metropolis'] text-[10px] text-[#51534A] mt-1.5">
            For internal audit only. Not sent to suppliers.
            {trimmedNotes.length > 0 && trimmedNotes.length < MIN_NOTES_LENGTH && (
              <> · {MIN_NOTES_LENGTH - trimmedNotes.length} more character{MIN_NOTES_LENGTH - trimmedNotes.length === 1 ? '' : 's'}.</>
            )}
          </p>
        </div>

        {/* Re-open checkbox — large clickable row */}
        <button
          type="button"
          onClick={() => canReopen && setAlsoReopen(v => !v)}
          disabled={!canReopen || submitting}
          aria-pressed={alsoReopen}
          className={`w-full text-left flex items-start gap-3 rounded-md px-3.5 py-3 mb-4 border transition-colors ${
            !canReopen
              ? 'bg-white border-line opacity-60 cursor-not-allowed'
              : alsoReopen
                ? 'bg-[#92D400]/[0.04] border-[#92D400]/40'
                : 'bg-white border-line hover:border-[#DADCCD] cursor-pointer'
          }`}>
          <span
            aria-hidden="true"
            className={`mt-0.5 w-[18px] h-[18px] rounded-sm flex items-center justify-center flex-shrink-0 border-2 ${
              alsoReopen ? 'bg-[#92D400] border-[#92D400]' : 'bg-transparent border-[#DADCCD]'
            }`}>
            {alsoReopen && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#131313" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-['Metropolis'] text-[13px] font-bold text-ink mb-0.5">Re-open this brief</span>
            <span className="block font-['Metropolis'] text-[11px] text-fusion-mid-grey leading-relaxed">
              {otherCount > 0
                ? <>Brief returns to "Awaiting Response" for the {otherCount} other supplier{otherCount === 1 ? '' : 's'} who originally pitched, and you can re-award once it's open. Untick if you're just withdrawing the award without re-opening.</>
                : <>Brief returns to active so you can add recipients and re-award. Untick if you're just withdrawing the award and leaving it closed.</>
              }
            </span>
          </span>
        </button>

        {/* Conditional deadline */}
        {alsoReopen && (
          <div className="mb-4 rounded-md px-3.5 py-3 bg-[#92D400]/[0.03] border border-[#92D400]/15">
            <label htmlFor="reopen-deadline" className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A] mb-1.5">
              New response deadline
            </label>
            <DatePicker
              id="reopen-deadline"
              selected={newDeadline}
              onChange={(d) => setNewDeadline(d)}
              dateFormat="dd/MM/yyyy"
              minDate={new Date(today.getTime() + 24 * 60 * 60 * 1000)}
              maxDate={eventDate || undefined}
              disabled={submitting}
              autoComplete="off"
              className="w-full bg-white border border-line rounded-md px-3 py-2.5 font-['Metropolis'] text-[13px] text-ink focus:outline-none focus:border-[#DADCCD] disabled:opacity-50" />
            <p className="font-['Metropolis'] text-[10px] text-[#51534A] mt-1.5">{deadlineHint}</p>
            {deadlineError && (
              <p className="font-['Metropolis'] text-[11px] text-[#E0290A] mt-1.5">{deadlineError}</p>
            )}
          </div>
        )}

        {/* No-automation reminder — amber. Names the supplier so the
            reminder is concrete, not abstract. */}
        <div className="rounded-md px-3.5 py-3 flex gap-3 items-start" style={{ background: 'rgba(232,154,46,0.06)', border: '1px solid rgba(232,154,46,0.3)' }}>
          <span className="text-[#7A4E10] flex-shrink-0 mt-0.5"><AlertIcon /></span>
          <div className="min-w-0 flex-1">
            <div className="font-['Metropolis'] text-[11px] font-extrabold uppercase tracking-[0.15em] text-[#7A4E10] mb-1">No automated emails</div>
            <p className="font-['Metropolis'] text-[12px] text-fusion-mid-grey leading-relaxed">
              Supply Pilot won't notify any suppliers automatically. <strong className="text-ink">Remember to contact {supplierName}</strong> directly{alsoReopen ? ', and reach out to any respondents you want to re-engage' : ''}.
            </p>
          </div>
        </div>

        {state === 'error' && errorMessage && (
          <div role="alert" className="mt-4 rounded-md px-3 py-2.5 font-['Metropolis'] text-[12px]" style={{ background: 'rgba(224,41,10,0.08)', border: '1px solid rgba(224,41,10,0.35)', color: '#E0290A' }}>
            {errorMessage}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={safeClose}
            disabled={submitting}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.18em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="font-['Metropolis'] text-[11px] font-extrabold uppercase tracking-[0.15em] text-ink bg-[#E0290A] border border-[#E0290A] rounded-full px-5 py-2 inline-flex items-center gap-2 transition-[filter,opacity] duration-150 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100">
            {submitting ? (<><Spinner /><span>{alsoReopen ? 'Re-opening…' : 'Withdrawing…'}</span></>) : <span>{submitLabel}</span>}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
